import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Check, ChevronRight, Sparkles, Trophy, X, HelpCircle, Coffee, Smile, BookOpen, Heart, Shuffle, Lightbulb, Clock, PartyPopper, RefreshCw, Flame } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { Button } from '../../components/ui/button'
import PullToRefresh from '../../components/PullToRefresh'
import Modal from '../../components/ui/Modal'
import { useAuth } from '../../contexts/AuthContext'
import { useConfirm } from '../../contexts/ConfirmDialogContext'
import { hasOwnerAccess, getPartnerId, getDisplayName } from '../../lib/owner'
import { supabase } from '../../lib/supabaseClient'
import { fireWinCelebration } from '../../lib/celebration'
import { cn } from '../../lib/utils'
import { findQuestion, scoreRound, ROUND_SIZE, CATEGORIES, DECK_SIZES } from '../../components/games/quiz/logic'
import { useQuizRounds, useAccuracyTally, useRecentRoundQuestionIds } from '../../components/games/quiz/hooks'
import { useQuestionPool } from '../../components/games/questions/hooks'
import Avatar from '../../components/Avatar'

const HOW_TO_SEEN_KEY = 'phoenix:quiz-howto-seen'
const DISMISSED_REVEALS_KEY = 'phoenix:quiz-dismissed-reveals'

// Per-device dismissed-reveal tracking. Tiny localStorage Set so a
// reveal the user has already seen doesn't re-pop into the banner on
// every page load. We trim to the last 200 ids so it can't grow
// unbounded — there's no scenario where they need history older than
// that for a "have I seen this?" check.
function loadDismissed() {
  try {
    const raw = window.localStorage.getItem(DISMISSED_REVEALS_KEY)
    if (!raw) return new Set()
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return new Set()
    return new Set(arr)
  } catch { return new Set() }
}

function saveDismissed(set) {
  try {
    const arr = Array.from(set).slice(-200)
    window.localStorage.setItem(DISMISSED_REVEALS_KEY, JSON.stringify(arr))
  } catch { /* noop */ }
}

export function QuizPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  useEffect(() => {
    if (authLoading) return
    if (!user || !hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, authLoading, navigate])

  const partnerId = useMemo(() => getPartnerId(user), [user])
  const partnerName = useMemo(() => getDisplayName(partnerId, locale), [partnerId, locale])

  const {
    rounds, answersByRound, loading: roundsLoading,
    startNewRound, abandonRound, restoreRound, appendAnswer, refetch,
  } = useQuizRounds(user?.id, partnerId)
  const confirm = useConfirm()

  const { pool } = useQuestionPool(user?.id)

  // Per-round derived state. One pass over the rounds plus their
  // answer rows so every downstream view picks fields off the same
  // shape.
  const enriched = useMemo(() => {
    if (!user?.id || !partnerId) return []
    return rounds.map((r) => {
      const ans = answersByRound.get(r.id) || []
      const myAns = ans.filter((a) => a.user_id === user.id)
      const theirAns = ans.filter((a) => a.user_id === partnerId)
      return {
        ...r,
        answers: ans,
        myProgress: Math.min(myAns.length, ROUND_SIZE),
        partnerProgress: Math.min(theirAns.length, ROUND_SIZE),
        myDone: myAns.length >= ROUND_SIZE,
        partnerDone: theirAns.length >= ROUND_SIZE,
        isClosed: !!r.completed_at,
      }
    })
  }, [rounds, answersByRound, user?.id, partnerId])

  // Active = oldest open round where I still owe answers. FIFO so
  // the partner catches up on Omar's queue from the bottom of the stack
  // upward; for Omar (who's always finishing rounds before piling
  // on more) there's only ever one element in this set anyway.
  const myActive = useMemo(() => {
    const candidates = enriched.filter((r) => !r.isClosed && !r.myDone)
    candidates.sort((a, b) => new Date(a.started_at) - new Date(b.started_at))
    return candidates[0] || null
  }, [enriched])

  // Rounds I've finished but my partner hasn't. Newest first so the
  // most recent waiting round is at the top of the list.
  const waitingOnPartner = useMemo(() => {
    const list = enriched.filter((r) => !r.isClosed && r.myDone && !r.partnerDone)
    list.sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
    return list
  }, [enriched])

  // Reveals I haven't seen yet: round is closed (DB trigger stamped
  // completed_at when both sides hit ROUND_SIZE) AND both actually
  // have ROUND_SIZE answers (filters out the legacy abandoned rounds)
  // AND I haven't dismissed locally. Oldest first so the banner
  // shows them in arrival order.
  const [dismissed, setDismissed] = useState(() => loadDismissed())
  const dismissReveal = useCallback((roundId) => {
    setDismissed((curr) => {
      if (curr.has(roundId)) return curr
      const next = new Set(curr); next.add(roundId)
      saveDismissed(next)
      return next
    })
  }, [])

  const revealsReady = useMemo(() => {
    const list = enriched.filter((r) => r.isClosed && r.myDone && r.partnerDone && !dismissed.has(r.id))
    list.sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at))
    return list
  }, [enriched, dismissed])

  // Recent question IDs from the last 3 rounds — exclusion set for
  // the round picker so consecutive rounds in the same deck don't
  // overlap heavily. Refreshes whenever any round closes or a new
  // round starts.
  const refreshKey = useMemo(
    () => `${rounds.length}:${rounds.filter((r) => r.completed_at).length}`,
    [rounds],
  )
  const recentRoundIds = useRecentRoundQuestionIds(user?.id, partnerId, 3, refreshKey)
  const accuracy = useAccuracyTally(user?.id, partnerId, refreshKey)

  // ---------- howto + reveal modals ----------
  const [howToOpen, setHowToOpen] = useState(false)
  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(HOW_TO_SEEN_KEY)
      if (!seen) setHowToOpen(true)
    } catch { /* noop */ }
  }, [])
  const dismissHowTo = () => {
    setHowToOpen(false)
    try { window.localStorage.setItem(HOW_TO_SEEN_KEY, '1') } catch { /* noop */ }
  }

  const [revealOpen, setRevealOpen] = useState(null) // round object or null
  const closeReveal = () => {
    if (revealOpen?.id) dismissReveal(revealOpen.id)
    setRevealOpen(null)
  }

  // Picker overlay state — triggered from "Play another while you
  // wait" or the reveal modal's "Start another" CTA.
  const [pickerOpen, setPickerOpen] = useState(false)
  const handlePick = useCallback(async (category) => {
    setPickerOpen(false)
    await startNewRound(category, pool, recentRoundIds)
  }, [startNewRound, pool, recentRoundIds])

  // Confetti — fire the first time a fresh reveal lands in
  // revealsReady. Track which round ids we've already celebrated so
  // re-renders don't refire.
  const celebratedRef = useRef(new Set())
  useEffect(() => {
    for (const r of revealsReady) {
      if (!celebratedRef.current.has(r.id)) {
        fireWinCelebration()
        celebratedRef.current.add(r.id)
        break
      }
    }
  }, [revealsReady])

  // In-flight cards combine "my turn" rounds + "waiting on partner"
  // rounds into a single list, newest first. The user picks which
  // round to enter — no more FIFO auto-display that locks them out
  // of the others. Declared BEFORE the early-return branches below
  // so the hook count stays invariant across renders (React #310
  // otherwise fires when auth flips loading→ready and the count
  // jumps).
  const inFlightCards = useMemo(() => {
    const list = enriched.filter((r) => !r.isClosed)
    list.sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
    return list
  }, [enriched])

  // Active-round mode: the user tapped a card and is now playing
  // that round. State (not URL) so the back button on the round view
  // just clears this and we're back on the list with all the other
  // in-flight rounds intact. Going back never abandons the round.
  const [activeRoundId, setActiveRoundId] = useState(null)
  const activeRound = useMemo(
    () => activeRoundId ? enriched.find((r) => r.id === activeRoundId) : null,
    [activeRoundId, enriched],
  )

  // When a fresh round is created, drop into it directly. The deck
  // picker dialog handles that hand-off via this callback.
  const startAndEnter = useCallback(async (category) => {
    setPickerOpen(false)
    const row = await startNewRound(category, pool, recentRoundIds)
    if (row?.id) setActiveRoundId(row.id)
  }, [startNewRound, pool, recentRoundIds])

  // Undo banner for the "skip this one" flow. After a confirmed
  // abandon we set undoTarget = {id}, render a small banner with an
  // Undo button, and auto-clear after UNDO_MS. Tapping Undo within
  // that window calls restoreRound which clears completed_at on the
  // row — the round returns to the in-flight list.
  const UNDO_MS = 6_000
  const [undoTarget, setUndoTarget] = useState(null) // { id, deckLabelKey }
  const undoTimerRef = useRef(null)

  const handleAbandonConfirmed = useCallback(async (roundId) => {
    const ok = await confirm({
      title: t('games.quiz.list.abandonConfirmTitle'),
      description: t('games.quiz.list.abandonConfirmBody', { name: partnerName }),
      confirmLabel: t('games.quiz.list.abandonConfirmYes'),
      cancelLabel: t('games.quiz.list.abandonConfirmCancel'),
      variant: 'destructive',
    })
    if (!ok) return
    const round = enriched.find((r) => r.id === roundId)
    await abandonRound(roundId)
    setUndoTarget({ id: roundId, category: round?.category || null })
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current)
    undoTimerRef.current = setTimeout(() => {
      setUndoTarget(null)
      undoTimerRef.current = null
    }, UNDO_MS)
  }, [confirm, t, enriched, abandonRound])

  const handleUndoAbandon = useCallback(async () => {
    if (!undoTarget) return
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current)
      undoTimerRef.current = null
    }
    const id = undoTarget.id
    setUndoTarget(null)
    await restoreRound(id)
  }, [undoTarget, restoreRound])

  useEffect(() => () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current)
  }, [])

  // Back-button policy: if the user is inside an active round, pop
  // back to the list view (clear activeRoundId). Only navigate
  // OUT of the quiz module when already on the list — otherwise
  // the top-right arrow and the inline "back to rounds" link
  // would do different things and fat-finger taps would drop the
  // user on /home mid-game.
  const goBack = () => {
    if (activeRoundId) {
      setActiveRoundId(null)
      return
    }
    navigate('/')
  }
  const handleRefresh = async () => { await refetch() }

  const localText = (obj) => (obj?.[locale] || obj?.en || '')

  // ---------- empty + no-partner states ----------
  if (!user || (roundsLoading && rounds.length === 0)) {
    return (
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="space-y-3 content-fade pb-8">
          <PageHeader t={t} goBack={goBack} accuracy={accuracy} onHowTo={() => setHowToOpen(true)} />
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-3 rounded-md border-2 border-border bg-card">
                <Skeleton className="w-8 h-8 rounded-md flex-shrink-0" />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <Skeleton className="h-3.5 w-24" />
                  <Skeleton className="h-3 w-40" />
                </div>
                <Skeleton className="w-4 h-4 rounded-full flex-shrink-0" />
              </div>
            ))}
          </div>
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
        {howToOpen && <HowToPlayDialog onClose={dismissHowTo} t={t} />}
      </PullToRefresh>
    )
  }

  if (!partnerId || !partnerName) {
    return (
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="space-y-3 content-fade pb-8">
          <PageHeader t={t} goBack={goBack} accuracy={accuracy} onHowTo={() => setHowToOpen(true)} />
          <Card>
            <CardContent className="p-6 text-center text-sm text-muted-foreground" dir="auto">
              {t('games.quiz.soloUnavailable')}
            </CardContent>
          </Card>
        </div>
        {howToOpen && <HowToPlayDialog onClose={dismissHowTo} t={t} />}
      </PullToRefresh>
    )
  }

  // ---------- main body ----------
  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        <PageHeader t={t} goBack={goBack} accuracy={accuracy} onHowTo={() => setHowToOpen(true)} />

        {revealsReady.length > 0 && (
          <RevealBanner
            t={t}
            count={revealsReady.length}
            onOpen={() => setRevealOpen(revealsReady[0])}
          />
        )}

        {activeRound ? (
          <ActiveRoundView
            t={t}
            user={user}
            round={activeRound}
            partnerName={partnerName}
            partnerId={partnerId}
            waitingCount={inFlightCards.filter((r) => r.id !== activeRound.id).length}
            localText={localText}
            pool={pool}
            onAppendAnswer={appendAnswer}
            onAbandon={null}
            onBack={() => setActiveRoundId(null)}
          />
        ) : (
          <RoundsList
            t={t}
            cards={inFlightCards}
            userId={user.id}
            partnerName={partnerName}
            partnerId={partnerId}
            onEnter={(id) => setActiveRoundId(id)}
            onAbandon={handleAbandonConfirmed}
            onStartNew={() => setPickerOpen(true)}
            onHistory={() => navigate('/games/quiz/history')}
            onRefresh={refetch}
          />
        )}

        {undoTarget && (
          <UndoAbandonToast
            t={t}
            onUndo={handleUndoAbandon}
            onClose={() => setUndoTarget(null)}
          />
        )}
      </div>

      {howToOpen && <HowToPlayDialog onClose={dismissHowTo} t={t} />}

      {pickerOpen && (
        <PickerDialog
          t={t}
          partnerName={partnerName}
          onClose={() => setPickerOpen(false)}
          onPick={startAndEnter}
        />
      )}

      {revealOpen && (
        <RevealDialog
          t={t}
          user={user}
          round={revealOpen}
          partnerName={partnerName}
          partnerId={partnerId}
          localText={localText}
          pool={pool}
          onClose={closeReveal}
          onStartAnother={() => {
            closeReveal()
            setPickerOpen(true)
          }}
        />
      )}
    </PullToRefresh>
  )
}

// Cards list — every in-flight round (mine to answer + waiting on
// partner) shown as a tap-to-enter card. "Start new round" + "View
// completed" sit below the list. The user can leave the round view
// any time and the round stays here intact.
function RoundsList({ t, cards, userId, partnerName, partnerId, onEnter, onAbandon, onStartNew, onHistory, onRefresh }) {
  return (
    <div className="space-y-3">
      {cards.length === 0 && (
        <div className="text-center py-6 space-y-3">
          <p className="text-sm text-muted-foreground" dir="auto">
            {t('games.quiz.list.empty', { name: partnerName })}
          </p>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-1.5 text-xs text-app-primary-500 hover:text-app-primary-400"
              dir="auto"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>{t('games.quiz.list.refresh')}</span>
            </button>
          )}
        </div>
      )}

      <div className="space-y-2">
        {cards.map((round) => (
          <RoundCard
            key={round.id}
            t={t}
            round={round}
            userId={userId}
            partnerName={partnerName}
            partnerId={partnerId}
            onEnter={() => onEnter(round.id)}
            onAbandon={() => onAbandon(round.id)}
          />
        ))}
      </div>

      <div className="space-y-2">
        <Button onClick={onStartNew} className="w-full">
          <Sparkles className="w-4 h-4 me-1" />
          {t('games.quiz.list.startNew')}
        </Button>
        <button
          type="button"
          onClick={onHistory}
          className="w-full text-center text-xs text-muted-foreground hover:text-foreground py-2 inline-flex items-center justify-center gap-1"
          dir="auto"
        >
          <span>{t('games.quiz.list.history')}</span>
          <ChevronRight className="w-3.5 h-3.5 rtl:-scale-x-100" />
        </button>
      </div>
    </div>
  )
}

function RoundCard({ t, round, userId, partnerName, partnerId, onEnter, onAbandon }) {
  const myTurn = !round.myDone
  const Icon = round.category ? (DECK_ICONS[round.category] || Sparkles) : Shuffle
  const deckLabel = round.category ? t(`games.quiz.deck.${round.category}.name`) : t('games.quiz.deck.surprise.name')
  const stateLabel = myTurn
    ? t('games.quiz.list.cardMyTurn', { progress: round.myProgress, total: ROUND_SIZE })
    : t('games.quiz.list.cardWaiting', { name: partnerName, progress: round.partnerProgress, total: ROUND_SIZE })

  return (
    <div
      className={cn(
        'rounded-md border-2 transition-colors',
        myTurn ? 'border-app-primary-500/40 bg-app-primary-500/5' : 'border-border bg-card',
      )}
    >
      <button
        type="button"
        onClick={onEnter}
        className="w-full text-start px-3 py-3 flex items-center gap-3"
        dir="auto"
      >
        <span className={cn(
          'p-2 rounded-md flex-shrink-0',
          myTurn ? 'bg-app-primary-500/10 text-app-primary-500' : 'bg-muted text-muted-foreground',
        )}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground" dir="auto">{deckLabel}</p>
          <p className="text-[11px] text-muted-foreground" dir="auto">{stateLabel}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100 flex-shrink-0" />
      </button>
      <button
        type="button"
        onClick={onAbandon}
        className="block w-full text-center text-[11px] text-muted-foreground hover:text-red-500 py-1.5 border-t border-border/40"
        dir="auto"
      >
        {t('games.quiz.list.cardAbandon')}
      </button>
    </div>
  )
}

// Inline undo banner — appears for ~6s after a confirmed abandon so
// a fat-fingered skip can be reversed without finding the round in
// some recovery list. Auto-dismisses via the parent's timer; the X
// button closes it early without restoring.
function UndoAbandonToast({ t, onUndo, onClose }) {
  return (
    <div
      className="flex items-center gap-2 rounded-md border-2 border-app-primary-500/40 bg-app-primary-500/10 px-3 py-2.5 mt-2"
      dir="auto"
      role="status"
    >
      <Clock className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
      <span className="flex-1 text-sm text-foreground" dir="auto">
        {t('games.quiz.list.undoToast')}
      </span>
      <button
        type="button"
        onClick={onUndo}
        className="text-sm font-semibold text-app-primary-500 hover:text-app-primary-400 px-2"
        dir="auto"
      >
        {t('games.quiz.list.undoCta')}
      </button>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('settings.close')}
        className="p-1 rounded-md text-muted-foreground hover:text-foreground"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

// Icons + tint per deck, plus a "Surprise me" mixed option at the
// end. Local to the QuizPage so the bank module stays UI-free.
const DECK_ICONS = {
  preferences: Coffee,
  dreams:      Sparkles,
  goofy:       Smile,
  memories:    BookOpen,
  spicy:       Heart,
  deep:        Lightbulb,
  intimate:    Flame,
}

function DeckPicker({ t, partnerName, onPick }) {
  return (
    <Card className="border-app-primary-500/30 bg-app-primary-500/5">
      <CardContent className="p-3 sm:p-4 space-y-3">
        <p className="text-sm text-foreground text-center" dir="auto">
          {t('games.quiz.startBlurb', { name: partnerName })}
        </p>
        <DeckList t={t} onPick={onPick} />
      </CardContent>
    </Card>
  )
}

function DeckList({ t, onPick }) {
  return (
    <div className="space-y-2">
      {CATEGORIES.map((cat) => {
        const Icon = DECK_ICONS[cat] || Sparkles
        return (
          <button
            key={cat}
            type="button"
            onClick={() => onPick(cat)}
            className="w-full text-start px-3 py-2.5 rounded-md border-2 border-border hover:bg-accent transition-colors flex items-center gap-3"
            dir="auto"
          >
            <span className="p-1.5 rounded-md bg-app-primary-500/10 text-app-primary-500 flex-shrink-0">
              <Icon className="w-4 h-4" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold text-foreground" dir="auto">
                {t(`games.quiz.deck.${cat}.name`)}
              </span>
              <span className="block text-[11px] text-muted-foreground" dir="auto">
                {t(`games.quiz.deck.${cat}.blurb`)} · {DECK_SIZES[cat]}
              </span>
            </span>
            <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100 flex-shrink-0" />
          </button>
        )
      })}
      <button
        type="button"
        onClick={() => onPick(null)}
        className="w-full text-start px-3 py-2.5 rounded-md border-2 border-dashed border-border hover:bg-accent transition-colors flex items-center gap-3"
        dir="auto"
      >
        <span className="p-1.5 rounded-md bg-app-primary-500/10 text-app-primary-500 flex-shrink-0">
          <Shuffle className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-foreground" dir="auto">
            {t('games.quiz.deck.surprise.name')}
          </span>
          <span className="block text-[11px] text-muted-foreground" dir="auto">
            {t('games.quiz.deck.surprise.blurb')}
          </span>
        </span>
        <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100 flex-shrink-0" />
      </button>
    </div>
  )
}

function PickerDialog({ t, partnerName, onClose, onPick }) {
  return (
    <Modal open onClose={onClose} ariaLabel={t('games.quiz.startCta')}>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">
          {t('games.quiz.startCta')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('settings.close')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <p className="text-sm text-muted-foreground mb-3" dir="auto">
        {t('games.quiz.startBlurb', { name: partnerName })}
      </p>
      <DeckList t={t} onPick={onPick} />
    </Modal>
  )
}

function PageHeader({ t, goBack, accuracy, onHowTo }) {
  const hasStats = accuracy.mineTotal + accuracy.theirsTotal > 0
  return (
    <div className="flex items-center gap-2 mb-2">
      <button
        type="button"
        onClick={goBack}
        aria-label={t('settings.back')}
        className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
      >
        <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
      </button>
      <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
        {t('games.quiz.title')}
      </h1>
      {hasStats && (
        <span
          className="inline-flex items-center gap-1 text-xs font-bold text-app-primary-500 font-mono tabular-nums"
          dir="ltr"
          title={t('games.quiz.runningTally')}
        >
          <Trophy className="w-3.5 h-3.5" />
          {accuracy.mineCorrect}/{accuracy.mineTotal}
        </span>
      )}
      <button
        type="button"
        onClick={onHowTo}
        aria-label={t('games.quiz.howToPlay')}
        className="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
      >
        <HelpCircle className="w-5 h-5" />
      </button>
    </div>
  )
}

function RevealBanner({ t, count, onOpen }) {
  const label = count > 1
    ? t('games.quiz.revealBannerMany', { count })
    : t('games.quiz.revealBanner')
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center gap-2 rounded-md border-2 border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5 text-start hover:bg-emerald-500/15 transition-colors"
      dir="auto"
    >
      <span className="p-1.5 rounded-md bg-emerald-500/20 text-emerald-500 flex-shrink-0">
        <PartyPopper className="w-4 h-4" />
      </span>
      <span className="flex-1 text-sm font-semibold text-foreground">{label}</span>
      <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100 flex-shrink-0" />
    </button>
  )
}

function ActiveRoundView({
  t, user, round, partnerName, partnerId, waitingCount,
  localText, pool, onAppendAnswer, onBack,
}) {
  const myAnsweredQuestionIds = useMemo(
    () => new Set(round.answers.filter((a) => a.user_id === user.id).map((a) => a.question_id)),
    [round.answers, user.id],
  )
  const nextQid = round.question_ids.find((qid) => !myAnsweredQuestionIds.has(qid))
  const nextQuestion = nextQid ? findQuestion(nextQid, pool) : null

  return (
    <>
      <div className="flex items-center gap-2">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            dir="auto"
          >
            <ChevronRight className="w-4 h-4 -scale-x-100 rtl:scale-x-100" />
            <span>{t('games.quiz.list.backToRounds')}</span>
          </button>
        )}
        <div className="flex-1" />
        <DeckChip t={t} category={round.category} />
      </div>
      <Card>
        <CardContent className="p-3 sm:p-4 space-y-2">
          <ProgressRow userId={user.id}   label={t('games.quiz.you')} done={round.myDone}      progress={round.myProgress}      total={ROUND_SIZE} />
          <ProgressRow userId={partnerId} label={partnerName}          done={round.partnerDone} progress={round.partnerProgress} total={ROUND_SIZE} />
        </CardContent>
      </Card>

      {waitingCount > 0 && (
        <div
          className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground"
          dir="auto"
        >
          <Clock className="w-3 h-3" />
          <span>{t('games.quiz.inFlightChip', { count: waitingCount, name: partnerName })}</span>
        </div>
      )}

      {nextQuestion && (
        <AnswerCard
          key={nextQuestion.id}
          t={t}
          user={user}
          round={round}
          question={nextQuestion}
          questionIndex={round.question_ids.indexOf(nextQid) + 1}
          totalQuestions={ROUND_SIZE}
          localText={localText}
          onAppendAnswer={onAppendAnswer}
        />
      )}

      {round.myDone && !round.partnerDone && (
        <Card>
          <CardContent className="p-3 sm:p-4 text-center text-xs text-muted-foreground" dir="auto">
            {t('games.quiz.waitingOnPartner', { name: partnerName })}
          </CardContent>
        </Card>
      )}
    </>
  )
}

function WaitingPanel({ t, partnerName, waitingRounds, onPlayAnother, onAbandon }) {
  const count = waitingRounds.length
  const headline = count > 1
    ? t('games.quiz.waitingPanelMany', { count, name: partnerName })
    : t('games.quiz.waitingPanelOne', { name: partnerName })

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex items-start gap-2" dir="auto">
          <span className="p-1.5 rounded-md bg-app-primary-500/10 text-app-primary-500 flex-shrink-0">
            <Clock className="w-4 h-4" />
          </span>
          <p className="flex-1 text-sm text-foreground">{headline}</p>
        </div>

        <ul className="space-y-1.5">
          {waitingRounds.map((r) => (
            <li
              key={r.id}
              className="flex items-center gap-2 text-[11px] text-muted-foreground rounded-md border border-border/60 px-2.5 py-1.5"
              dir="auto"
            >
              <DeckTag t={t} category={r.category} />
              <span className="flex-1 truncate">
                {t('games.quiz.partnerProgressLine', {
                  progress: r.partnerProgress,
                  total: ROUND_SIZE,
                  name: partnerName,
                })}
              </span>
              <button
                type="button"
                onClick={() => onAbandon(r.id)}
                className="text-[11px] text-muted-foreground hover:text-red-500 px-1"
                dir="auto"
              >
                {t('games.quiz.skipOne')}
              </button>
            </li>
          ))}
        </ul>

        <Button onClick={onPlayAnother} className="w-full">
          {t('games.quiz.playAnotherWhileWait')}
          <ChevronRight className="w-4 h-4 ms-1 rtl:-scale-x-100" />
        </Button>
      </CardContent>
    </Card>
  )
}

// Inline-tag variant of the deck chip — same icon/label as DeckChip
// but compact, for use inside the waiting list.
function DeckTag({ t, category }) {
  const isMixed = !category
  const Icon = isMixed ? Shuffle : (DECK_ICONS[category] || Sparkles)
  const label = isMixed
    ? t('games.quiz.deck.surprise.name')
    : t(`games.quiz.deck.${category}.name`)
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-app-primary-500 flex-shrink-0" dir="auto">
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </span>
  )
}

// Tiny tag-style chip showing which deck a round was pulled from.
function DeckChip({ t, category }) {
  const isMixed = !category
  const Icon = isMixed ? Shuffle : (DECK_ICONS[category] || Sparkles)
  const label = isMixed
    ? t('games.quiz.deck.surprise.name')
    : t(`games.quiz.deck.${category}.name`)
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" dir="auto">
      <Icon className="w-3.5 h-3.5 text-app-primary-500" />
      <span>{t('games.quiz.deckLabel', { name: label })}</span>
    </div>
  )
}

function ProgressRow({ label, done, progress, total, userId }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        {done
          ? <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          : <span className="w-4 h-4 rounded-full bg-app-primary-500/30 animate-pulse flex-shrink-0" />}
        <Avatar userId={userId} size={20} />
        <span className="text-foreground font-medium truncate" dir="auto">{label}</span>
      </div>
      <span className="text-muted-foreground font-mono tabular-nums flex-shrink-0" dir="ltr">{progress}/{total}</span>
    </div>
  )
}

function AnswerCard({ t, user, round, question, questionIndex, totalQuestions, localText, onAppendAnswer }) {
  const [selfChoice, setSelfChoice] = useState(null)
  const [guess, setGuess] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const canSubmit = selfChoice && guess && !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    const { data, error: insertError } = await supabase
      .from('quiz_answers')
      .insert({
        round_id: round.id,
        user_id: user.id,
        question_id: question.id,
        self_choice_id: selfChoice,
        partner_guess_id: guess,
      })
      .select()
      .single()
    setSubmitting(false)
    if (insertError || !data) {
      setError(t('games.quiz.submitFailed'))
      return
    }
    onAppendAnswer?.(round.id, data)
    setSelfChoice(null)
    setGuess(null)
    // Fire-and-forget push so the partner knows progress on Q1 + done.
    if (questionIndex === 1 || questionIndex === totalQuestions) {
      supabase.functions
        .invoke('couples-quiz-partner-push', {
          body: {
            round_id: round.id,
            kind: questionIndex === totalQuestions ? 'finished' : 'started',
          },
        })
        .catch(() => { /* push is best-effort */ })
    }
  }

  return (
    <Card className="border-app-primary-500/30 bg-app-primary-500/5">
      <CardContent className="p-3 sm:p-4 space-y-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-app-primary-500" dir="auto">
          {t('games.quiz.questionN', { n: questionIndex, total: totalQuestions })}
        </p>
        <p className="text-base font-semibold text-foreground" dir="auto">
          {localText(question.text)}
        </p>

        <ChoiceGroup
          t={t}
          label={t('games.quiz.yourAnswer')}
          options={question.options}
          selected={selfChoice}
          onSelect={setSelfChoice}
          localText={localText}
        />

        <ChoiceGroup
          t={t}
          label={t('games.quiz.guessTheirs')}
          options={question.options}
          selected={guess}
          onSelect={setGuess}
          localText={localText}
        />

        {error && (
          <p className="text-xs text-red-500 text-center" dir="auto">{error}</p>
        )}

        <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full">
          {submitting
            ? t('games.quiz.submitting')
            : questionIndex === totalQuestions
              ? t('games.quiz.submitFinal')
              : t('games.quiz.submitNext')}
          {!submitting && <ChevronRight className="w-4 h-4 ms-1 rtl:-scale-x-100" />}
        </Button>
      </CardContent>
    </Card>
  )
}

function ChoiceGroup({ label, options, selected, onSelect, localText }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" dir="auto">{label}</p>
      <div className="space-y-1.5">
        {options.map((opt) => {
          const isSelected = selected === opt.id
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onSelect(opt.id)}
              className={cn(
                'w-full text-start px-3 py-2 rounded-md border-2 text-sm transition-colors',
                isSelected
                  ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500 font-semibold'
                  : 'border-border text-foreground hover:bg-accent',
              )}
              dir="auto"
            >
              <span className="flex items-center gap-2">
                {isSelected && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                <span className="flex-1">{localText(opt.text)}</span>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function RevealDialog({ t, user, round, partnerName, partnerId, localText, pool, onClose, onStartAnother }) {
  return (
    <Modal open onClose={onClose} ariaLabel={t('games.quiz.bothDone')}>
      <div className="flex items-center gap-2 mb-3">
        <DeckChip t={t} category={round.category} />
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          aria-label={t('settings.close')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <ResultsCard
        t={t}
        user={user}
        round={round}
        answers={round.answers}
        partnerId={partnerId}
        partnerName={partnerName}
        localText={localText}
        pool={pool}
        onStartAnother={onStartAnother}
      />
    </Modal>
  )
}

function ResultsCard({ t, user, round, answers, partnerId, partnerName, localText, pool, onStartAnother }) {
  const myScore     = scoreRound(answers, round.question_ids, user.id, partnerId)
  const partnerScore = scoreRound(answers, round.question_ids, partnerId, user.id)
  const myMap = new Map(answers.filter((a) => a.user_id === user.id).map((a) => [a.question_id, a]))
  const theirMap = new Map(answers.filter((a) => a.user_id === partnerId).map((a) => [a.question_id, a]))

  return (
    <Card className="border-app-primary-500/30 bg-app-primary-500/5">
      <CardContent className="p-3 sm:p-4 space-y-3">
        <p className="text-sm font-semibold text-foreground text-center" dir="auto">
          {t('games.quiz.bothDone')}
        </p>
        <div className="grid grid-cols-2 gap-2 text-center">
          <ScoreBlock t={t} userId={user.id}    label={t('games.quiz.you')} score={myScore}     about={partnerName} />
          <ScoreBlock t={t} userId={partnerId}  label={partnerName}          score={partnerScore} about={t('games.quiz.you')} />
        </div>
        <div className="space-y-3">
          {round.question_ids.map((qid, i) => {
            const q = findQuestion(qid, pool)
            if (!q) return null
            const mine = myMap.get(qid)
            const theirs = theirMap.get(qid)
            const myGuessCorrect = mine?.partner_guess_id === theirs?.self_choice_id
            const theirGuessCorrect = theirs?.partner_guess_id === mine?.self_choice_id
            return (
              <div key={qid} className="space-y-1 border-t border-border/60 pt-2 first:border-0 first:pt-0">
                <p className="text-[11px] text-muted-foreground" dir="auto">{i + 1}. {localText(q.text)}</p>
                <ReviewRow
                  userId={user.id}
                  label={t('games.quiz.you')}
                  selfText={localText(q.options.find((o) => o.id === mine?.self_choice_id)?.text)}
                  guessText={localText(q.options.find((o) => o.id === mine?.partner_guess_id)?.text)}
                  guessAbout={partnerName}
                  guessCorrect={myGuessCorrect}
                  t={t}
                />
                <ReviewRow
                  userId={partnerId}
                  label={partnerName}
                  selfText={localText(q.options.find((o) => o.id === theirs?.self_choice_id)?.text)}
                  guessText={localText(q.options.find((o) => o.id === theirs?.partner_guess_id)?.text)}
                  guessAbout={t('games.quiz.you')}
                  guessCorrect={theirGuessCorrect}
                  t={t}
                />
              </div>
            )
          })}
        </div>
        <Button onClick={onStartAnother} className="w-full">
          {t('games.quiz.startAnother')}
        </Button>
      </CardContent>
    </Card>
  )
}

function ScoreBlock({ t, userId, label, score, about }) {
  return (
    <div className="rounded-lg border border-border bg-card p-2 sm:p-3">
      {/* `about` is interpolated into the i18n sentence "Guesses about
          X" → no avatar (per the rule: skip when the name is in
          prose). The `label` below the score IS standalone, so its
          avatar travels with it. */}
      <p className="text-[11px] text-muted-foreground" dir="auto">{t('games.quiz.knewAbout', { name: about })}</p>
      <p className="text-lg font-bold text-foreground font-mono tabular-nums" dir="ltr">
        {score ? `${score.correct}/${score.total}` : '—'}
      </p>
      <div className="flex items-center justify-center gap-1.5" dir="auto">
        <Avatar userId={userId} size={14} />
        <p className="text-[10px] text-muted-foreground">{label}</p>
      </div>
    </div>
  )
}

function ReviewRow({ userId, label, selfText, guessText, guessAbout, guessCorrect, t }) {
  return (
    <div className="text-xs space-y-0.5" dir="auto">
      <p className="text-foreground flex items-center gap-1.5">
        <Avatar userId={userId} size={14} />
        <span className="text-muted-foreground">{label}:</span>
        <span>{selfText || '—'}</span>
      </p>
      <p className={cn('flex items-center gap-1 ms-[22px]', guessCorrect ? 'text-emerald-500' : 'text-stone-500')}>
        {guessCorrect ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
        {/* `guessAbout` is in the i18n sentence "You guessed they'd
            pick X" — sentence, no avatar. */}
        <span>{t('games.quiz.guessedThem', { name: guessAbout, choice: guessText || '—' })}</span>
      </p>
    </div>
  )
}

function HowToPlayDialog({ onClose, t }) {
  return (
    <Modal open onClose={onClose} ariaLabel={t('games.quiz.howToPlay')}>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">
          {t('games.quiz.howToPlay')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('settings.close')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <ul className="space-y-2 text-sm text-foreground" dir="auto">
        <li>{t('games.quiz.howTo.line1')}</li>
        <li>{t('games.quiz.howTo.line2')}</li>
        <li>{t('games.quiz.howTo.line3')}</li>
        <li>{t('games.quiz.howTo.line4')}</li>
      </ul>
      <button
        type="button"
        onClick={onClose}
        className="w-full mt-4 py-2 rounded-md bg-app-primary-500 text-white font-semibold"
      >
        {t('games.quiz.howTo.cta')}
      </button>
    </Modal>
  )
}
